import { useState, useEffect, useMemo } from 'react';
import api, { downloadCsv, exportIdsParam } from '../utils/api';
import ClearFiltersButton from '../components/common/ClearFiltersButton';
import { useAuth } from '../context/AuthContext';
import { useUrlParams } from '../hooks/useUrlState';
import Pagination from '../components/Pagination';
import Badge from '../components/Badge';
import ActionsDropdown from '../components/ActionsDropdown';
import StateBlock from '../components/StateBlock';
import CsvImportModal from '../components/CsvImportModal';
import ClientPanel from '../components/ClientPanel';
import ClientEditDrawer from '../components/ClientEditDrawer';
import AlphaFilter from '../components/common/AlphaFilter';

export default function ClientsPage() {
  const { user } = useAuth();
  const [clients, setClients] = useState(null);
  const [error, setError] = useState(false);
  const { get, set } = useUrlParams();
  const search = get('search', '');
  const alphaFilter = get('alpha', '');
  const page = get('page', 1);
  const pageSize = get('pageSize', 25);

  const [panelId, setPanelId] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [showImport, setShowImport] = useState(false);

  function load() {
    setError(false);
    api.get('/clients')
      .then(r => setClients(r.data))
      .catch(() => setError(true));
  }

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!clients) return [];
    return clients.filter(c => {
      if (search && !c.name.toLowerCase().includes(search.toLowerCase()) &&
        !(c.contact_name || '').toLowerCase().includes(search.toLowerCase())) return false;
      if (alphaFilter && c.name.charAt(0).toUpperCase() !== alphaFilter) return false;
      return true;
    });
  }, [clients, search, alphaFilter]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  function handleFilterChange(key, value) {
    set({ [key]: value, page: 1 });
  }

  async function handleExport() {
    await downloadCsv('/clients/export', 'clients-export.csv', exportIdsParam(filtered, clients));
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            Client Management {clients && <span className="page-title-count">({clients.length} total)</span>}
          </h1>
          <p className="page-subtitle">Manage client records, billing terms, and engagement history.</p>
        </div>
        <div className="page-actions">
          {user?.role === 'admin' && (
            <button className="btn btn-secondary" onClick={handleExport}>↓ Export CSV</button>
          )}
          <button className="btn btn-secondary" onClick={() => setShowImport(true)}>↑ Upload CSV</button>
          <button className="btn btn-primary" onClick={() => setEditTarget({})}>+ Add Client</button>
        </div>
      </div>

      <div className="filter-bar">
        <input
          type="text"
          className="filter-search"
          placeholder="Search client or contact…"
          value={search}
          onChange={e => handleFilterChange('search', e.target.value)}
        />
        <ClearFiltersButton active={!!(search || alphaFilter)} onClear={() => set({ search: '', alpha: '', page: 1 })} />
      </div>
      <AlphaFilter items={clients || []} value={alphaFilter} onChange={l => handleFilterChange('alpha', l)} />

      {error && <StateBlock type="error" action={<button className="btn btn-primary" onClick={load}>Try again</button>} />}
      {!error && clients === null && <StateBlock type="loading" />}
      {!error && clients !== null && filtered.length === 0 && (
        <StateBlock type="noResults" message="No clients match this search. Clear filters or add a client." />
      )}

      {!error && filtered.length > 0 && (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th>Client Name</th>
                <th>Contact</th>
                <th>Email</th>
                <th>Net Terms</th>
                <th>Active WOs</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map(c => (
                <tr key={c.id}>
                  <td>
                    <button className="row-name-link" onClick={() => setPanelId(c.id)}>{c.name}</button>
                  </td>
                  <td>{c.contact_name || '—'}</td>
                  <td>{c.email || '—'}</td>
                  <td>Net {c.net_terms}</td>
                  <td>{c.active_wo_count}</td>
                  <td style={{ textAlign: 'right' }}>
                    <ActionsDropdown actions={[
                      { label: 'View', onClick: () => setPanelId(c.id) },
                      { label: 'Edit', onClick: () => setEditTarget(c) },
                    ]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={page} pageSize={pageSize} total={filtered.length}
            onPageChange={(p) => set({ page: p })}
            onPageSizeChange={(s) => set({ pageSize: s, page: 1 })}
          />
        </>
      )}

      {panelId && (
        <ClientPanel
          clientId={panelId}
          onClose={() => setPanelId(null)}
          onEdit={(data) => { setPanelId(null); setEditTarget(data); }}
        />
      )}

      {editTarget !== null && (
        <ClientEditDrawer
          client={editTarget.id ? editTarget : null}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); }}
        />
      )}

      {showImport && (
        <CsvImportModal
          entityLabel="Client"
          importPath="/clients/import"
          onClose={() => setShowImport(false)}
          onComplete={load}
        />
      )}
    </div>
  );
}
